import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq } from 'ember-truth-helpers';
import type Owner from '@ember/owner';
import type { Store } from '@warp-drive/core';
import type Session from '../services/session';
import type Notifications from '../services/notifications';
import type { UserItem } from '../schemas/user-item.ts';
import type { Tag } from '../schemas/tag.ts';
import type { PaginationMeta } from '../utils/api-types.ts';
import {
  getUserItems,
  updateUserItemStatus,
  getTags,
} from '../builders/profile';
import ItemCard from './item-card.gts';
import { type ItemResource } from './item-card/utils.gts';
import { VerticalCollection } from '@html-next/vertical-collection';
import Icon from './icon';
import tw from '../helpers/tw';
import TagIcon from '~icons/lucide/tag';
import pullToRefresh from '../modifiers/pull-to-refresh.ts';
import responsiveColumns from '../modifiers/responsive-columns.ts';
import { chunkArray } from '../utils/chunk-array.ts';
import InfiniteScrollTrigger from './infinite-scroll-trigger.gts';

type StatusFilter = '' | 'active' | 'watched' | 'dismissed';

interface MyItemsListSignature {
  Element: HTMLDivElement;
}

/** Map embedded item data to an ItemResource shape for ItemCard */
function toItemResource(userItem: UserItem): ItemResource {
  const item = userItem.item as Record<string, unknown> | null;
  if (!item) {
    return { id: userItem.id } as ItemResource;
  }
  return {
    id: item['id'] as string,
    ...item,
  } as unknown as ItemResource;
}

export default class MyItemsList extends Component<MyItemsListSignature> {
  @service declare store: Store;
  @service declare session: Session;
  @service declare notifications: Notifications;

  @tracked userItems: UserItem[] = [];
  @tracked tags: Tag[] = [];
  @tracked meta: PaginationMeta = { page: 1, totalPages: 1 };
  @tracked isLoading = true;
  @tracked isLoadingMore = false;
  @tracked activeStatus: StatusFilter = '';
  @tracked activeType = '';
  @tracked activeTagId = '';
  @tracked actionInProgress = '';
  @tracked columnCount = 1;

  readonly gridBreakpoints: [number, number][] = [
    [1024, 3],
    [640, 2],
    [1, 1],
  ];

  constructor(owner: Owner, args: object) {
    super(owner, args);
    void this.loadData();
  }

  get hasMore() {
    return this.meta.page < this.meta.totalPages;
  }

  @action
  setColumnCount(n: number) {
    this.columnCount = n;
  }

  get gridRowClass(): string {
    if (this.columnCount === 3) return 'mb-6 grid grid-cols-3 gap-6';
    if (this.columnCount === 2) return 'mb-6 grid grid-cols-2 gap-6';
    return 'mb-6 grid grid-cols-1 gap-6';
  }

  /** Returns items annotated with whether they fall outside the active status tab */
  @cached
  get annotatedItems() {
    return this.userItems.map((item) => ({
      id: item.id,
      item,
      isOutsideStatusTab: this.checkIsOutsideStatusTab(item),
    }));
  }

  @cached
  get rows() {
    return chunkArray(this.annotatedItems, this.columnCount);
  }

  private checkIsOutsideStatusTab(userItem: UserItem): boolean {
    const status = userItem.status ?? 'active';
    if (this.activeStatus === '') {
      // "All" tab excludes dismissed items by default
      return status === 'dismissed';
    }
    return status !== this.activeStatus;
  }

  private applyStatusUpdate(targetId: string, newStatus: string): void {
    const current = this.userItems.find((item) => item.id === targetId);
    if (!current) return;

    const wasOutside = this.checkIsOutsideStatusTab(current);
    const willBeOutside = this.checkIsOutsideStatusTab({
      ...current,
      status: newStatus,
    });

    this.userItems = this.userItems.map((item) =>
      item.id === targetId ? { ...item, status: newStatus } : item
    );

    if (wasOutside !== willBeOutside && this.meta.total !== undefined) {
      const delta = willBeOutside ? -1 : 1;
      const newTotal = Math.max(0, this.meta.total + delta);
      const pageSize = this.meta.pageSize ?? 24;
      this.meta = {
        ...this.meta,
        total: newTotal,
        totalPages: Math.max(1, Math.ceil(newTotal / pageSize)),
      };
    }
  }

  get statuses(): { key: StatusFilter; label: string }[] {
    return [
      { key: '', label: 'All' },
      { key: 'active', label: 'Active' },
      { key: 'watched', label: '✓ Done' },
      { key: 'dismissed', label: 'Dismissed' },
    ];
  }

  get types() {
    return [
      { key: '', label: 'All types' },
      { key: 'movie', label: 'Movies' },
      { key: 'tv_show', label: 'TV Shows' },
      { key: 'book', label: 'Books' },
      { key: 'music', label: 'Music' },
      { key: 'podcast', label: 'Podcasts' },
      { key: 'place', label: 'Places' },
      { key: 'product', label: 'Products' },
      { key: 'video_game', label: 'Games' },
    ];
  }

  @action
  async loadData() {
    this.isLoading = true;
    try {
      // Personal lists that change right after an extraction — always refetch
      // (also makes pull-to-refresh actually refresh).
      const [itemsResult, tagsResult] = await Promise.all([
        this.store.request({
          ...getUserItems({
            status: this.activeStatus || undefined,
            type: this.activeType || undefined,
            tagId: this.activeTagId || undefined,
            dismissed: this.activeStatus === 'dismissed' ? 'true' : undefined,
          }),
          cacheOptions: { reload: true },
        }),
        this.store.request({ ...getTags(), cacheOptions: { reload: true } }),
      ]);

      const itemsJson = itemsResult.content as {
        data?: UserItem[];
        meta?: PaginationMeta;
      };
      const tagsJson = tagsResult.content as {
        data?: Tag[];
      };

      this.userItems = itemsJson.data ?? [];
      this.meta = itemsJson.meta ?? { page: 1, totalPages: 1 };
      this.tags = tagsJson.data ?? [];
    } catch {
      this.notifications.error('Failed to load items');
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async loadMore() {
    if (this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    try {
      const nextPage = this.meta.page + 1;
      const result = await this.store.request({
        ...getUserItems({
          page: nextPage.toString(),
          status: this.activeStatus || undefined,
          type: this.activeType || undefined,
          tagId: this.activeTagId || undefined,
          dismissed: this.activeStatus === 'dismissed' ? 'true' : undefined,
        }),
        cacheOptions: { reload: true },
      });

      const json = result.content as {
        data?: UserItem[];
        meta?: PaginationMeta;
      };

      this.userItems = [...this.userItems, ...(json.data ?? [])];
      this.meta = json.meta ?? this.meta;
    } finally {
      this.isLoadingMore = false;
    }
  }

  @action
  filterByStatus(status: StatusFilter) {
    this.activeStatus = status;
    void this.loadData();
  }

  @action
  changeType(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.activeType = target.value;
    void this.loadData();
  }

  @action
  filterByTag(tagId: string) {
    this.activeTagId = this.activeTagId === tagId ? '' : tagId;
    void this.loadData();
  }

  @action
  async markDone(userItem: UserItem) {
    this.actionInProgress = userItem.id ?? '';
    try {
      await this.store.request(updateUserItemStatus(userItem.id!, 'watched'));
      this.applyStatusUpdate(userItem.id!, 'watched');
      this.notifications.success('Marked as done');
    } catch {
      this.notifications.error('Failed to update');
    } finally {
      this.actionInProgress = '';
    }
  }

  @action
  async dismiss(userItem: UserItem) {
    this.actionInProgress = userItem.id ?? '';
    try {
      await this.store.request(updateUserItemStatus(userItem.id!, 'dismissed'));
      this.applyStatusUpdate(userItem.id!, 'dismissed');
      this.notifications.success('Item dismissed');
    } catch {
      this.notifications.error('Failed to dismiss');
    } finally {
      this.actionInProgress = '';
    }
  }

  @action
  async restore(userItem: UserItem) {
    this.actionInProgress = userItem.id ?? '';
    try {
      await this.store.request(updateUserItemStatus(userItem.id!, 'active'));
      this.applyStatusUpdate(userItem.id!, 'active');
      this.notifications.success('Item restored');
    } catch {
      this.notifications.error('Failed to restore');
    } finally {
      this.actionInProgress = '';
    }
  }

  <template>
    <div ...attributes {{pullToRefresh this.loadData}}>
      {{! Filter bar }}
      <div class="mb-4 flex flex-wrap items-center gap-2">
        {{! Status tabs }}
        <div class="tabs tabs-box">
          {{#each this.statuses as |s|}}
            <button
              type="button"
              class={{tw "tab" (if (eq this.activeStatus s.key) "tab-active")}}
              {{on "click" (fn this.filterByStatus s.key)}}
            >
              {{s.label}}
            </button>
          {{/each}}
        </div>

        {{! Type select }}
        <select
          class="select select-bordered select-sm"
          aria-label="Filter by type"
          {{on "change" this.changeType}}
        >
          {{#each this.types as |t|}}
            <option value={{t.key}} selected={{eq this.activeType t.key}}>
              {{t.label}}
            </option>
          {{/each}}
        </select>

        {{! Tag filter pills }}
        {{#if this.tags.length}}
          <div class="flex flex-wrap gap-1">
            {{#each this.tags as |tag|}}
              <button
                type="button"
                class={{tw
                  "badge badge-sm cursor-pointer"
                  (if
                    (eq this.activeTagId tag.id) "badge-primary" "badge-outline"
                  )
                }}
                {{on "click" (fn this.filterByTag tag.id)}}
              >
                <Icon @icon={{TagIcon}} class="mr-1 h-3 w-3" />
                {{tag.label}}
              </button>
            {{/each}}
          </div>
        {{/if}}
      </div>

      {{! Items grid }}
      {{#if this.isLoading}}
        <div class="flex justify-center py-12">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      {{else if this.userItems.length}}
        <div
          {{responsiveColumns
            onChange=this.setColumnCount
            breakpoints=this.gridBreakpoints
          }}
        >
          <VerticalCollection
            @items={{this.rows}}
            @estimateHeight={{172}}
            @staticHeight={{true}}
            @containerSelector="body"
            @tagName="div"
            as |row|
          >
            <div class={{this.gridRowClass}}>
              {{#each row as |annotated|}}
                <ItemCard
                  class={{if
                    annotated.isOutsideStatusTab
                    "opacity-40 grayscale transition-all"
                  }}
                  @item={{toItemResource annotated.item}}
                  @userItem={{annotated.item}}
                  @onDone={{fn this.markDone annotated.item}}
                  @onDismiss={{fn this.dismiss annotated.item}}
                  @onRestore={{fn this.restore annotated.item}}
                  @disabled={{eq this.actionInProgress annotated.item.id}}
                  @tags={{this.tags}}
                  @onTagsChanged={{this.loadData}}
                />
              {{/each}}
            </div>
          </VerticalCollection>
        </div>

        <InfiniteScrollTrigger
          @hasMore={{this.hasMore}}
          @isLoading={{this.isLoadingMore}}
          @onLoadMore={{this.loadMore}}
        />
      {{else}}
        <div class="text-base-content/60 py-12 text-center">
          {{#if this.activeStatus}}
            No items with this status.
          {{else}}
            No items in your collection yet. Items from your submitted sources
            will appear here automatically.
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
